import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { waLink } from '@/data/site';
import { useBusiness } from '@/lib/business';
import { useReveal } from '@/lib/useReveal';
import { PageHeader } from '@/components/ui/PageHeader';

export function Contact() {
  useReveal();
  const SITE = useBusiness();
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');

  const mapsQuery = encodeURIComponent(`${SITE.address}, ${SITE.country}`);

  function mailtoHref(): string {
    const subject = encodeURIComponent(`Enquiry from ${name || 'a guest'}`);
    const body = encodeURIComponent(`${message}\n\n— ${name}`);
    return `mailto:${SITE.contactEmail}?subject=${subject}&body=${body}`;
  }

  return (
    <>
      <Seo title="Contact" description={`Visit Grace Haven Spa at ${SITE.address}, Gaborone. Call or WhatsApp ${SITE.phone}.`} />

      <PageHeader eyebrow="Say hello" title="Contact" />

      <section className="bg-cream py-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-2">
            {/* Details */}
            <div className="reveal space-y-8">
              <div>
                <h2 className="font-display text-3xl text-espresso">Find us</h2>
                <div className="rule-gold mt-4" />
              </div>
              <ul className="space-y-5 font-body text-muted">
                <li className="flex gap-4"><MapPin className="mt-0.5 shrink-0 text-gold-deep" size={20} /><span><span className="block font-medium text-espresso">Address</span>{SITE.address}</span></li>
                <li className="flex gap-4"><Phone className="mt-0.5 shrink-0 text-gold-deep" size={20} /><span><span className="block font-medium text-espresso">Call / WhatsApp</span><a href={`tel:${SITE.phoneDial}`} className="hover:text-gold-deep">{SITE.phone}</a></span></li>
                <li className="flex gap-4"><Mail className="mt-0.5 shrink-0 text-gold-deep" size={20} /><span><span className="block font-medium text-espresso">Email</span><a href={`mailto:${SITE.contactEmail}`} className="hover:text-gold-deep">{SITE.contactEmail}</a></span></li>
                <li className="flex gap-4">
                  <Clock className="mt-0.5 shrink-0 text-gold-deep" size={20} />
                  <span>
                    <span className="block font-medium text-espresso">Hours</span>
                    {SITE.hours.map((h) => (<span key={h.day} className="block">{h.day}: {h.time}</span>))}
                  </span>
                </li>
              </ul>
              <a href={waLink(SITE.whatsapp, 'Hi Grace Haven, I have a question.')} target="_blank" rel="noreferrer" className="btn-gold">
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
            </div>

            {/* Quick message + map */}
            <div className="reveal space-y-6">
              <div className="border border-line bg-paper p-7">
                <h3 className="font-display text-2xl text-espresso">Send a quick message</h3>
                <p className="mt-1 font-body text-sm text-muted">Opens your email app, addressed to us.</p>
                <div className="mt-5 space-y-4">
                  <div>
                    <label className="label" htmlFor="c-name">Your name</label>
                    <input id="c-name" className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane" />
                  </div>
                  <div>
                    <label className="label" htmlFor="c-msg">Message</label>
                    <textarea id="c-msg" rows={4} className="field" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="I'd like to ask about…" />
                  </div>
                  <a href={mailtoHref()} className="btn-primary w-full">Send message</a>
                </div>
              </div>

              <div className="overflow-hidden border border-line">
                <iframe
                  title="Grace Haven Spa location"
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
                />
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
